import _ from 'lodash';
import { toast } from 'vue3-toastify';
import { no_answers_strings } from '@/assets/js/no_answers_strings.js';
import { generatorErrorCode } from '@/assets/js/constants.js';
import { extractAnswerAndReasoning } from '@/composables/useAnswerExtraction';

function isCursorInsideImage(text, cursorPosition) {
    const pattern = /!\[.*?\]\(.*?\)/g;
    let match;
    while ((match = pattern.exec(text)) !== null) {
        const start = match.index;
        const end = pattern.lastIndex;
        if (cursorPosition > start && cursorPosition <= end) {
            return { inside: true, endPosition: end - 1 };
        }
    }
    return { inside: false };
}

const processData = ({ buffer, data }) => {
    const finished_jsons = [];
    const jsonStrings = (buffer + data).split('}{');
    // fix json strings
    if (jsonStrings.length > 1) {
        for (let i = 0; i < jsonStrings.length; i++) {
            if (i === 0) {
                jsonStrings[i] = jsonStrings[i] + '}';
            } else if (i === jsonStrings.length - 1) {
                jsonStrings[i] = '{' + jsonStrings[i];
            } else {
                jsonStrings[i] = '{' + jsonStrings[i] + '}';
            }
        }
    }

    if (jsonStrings.length === 1) {
        try {
            const jsonData = JSON.parse(jsonStrings[0]);
            finished_jsons.push(jsonData);
        } catch (error) {
            buffer += jsonStrings[0];
        }
        return { buffer, json: finished_jsons };
    }
    // Process each JSON string
    for (let i = 0; i < jsonStrings.length; i++) {
        const jsonString = jsonStrings[i];
        try {
            const jsonData = JSON.parse(jsonString);
            finished_jsons.push(jsonData);
        } catch (error) {
            // If there is an error, it means the JSON string is incomplete
            if (i === jsonStrings.length - 1) {
                buffer += jsonString;
            }
        }
    }
    return { buffer, json: finished_jsons };
};

export const consume_stream = async ({
    url = undefined,
    data = undefined,
    initialData = (data) => data,
    dataStream = (data) => data,
    citedData = (data) => data,
    no_answer_callback = async () => {},
    finished_callback = async () => {},
    expect_first_chunk = true,
    response_language = 'en',
    query_language = 'en',
    no_rag_fallback = false,
    auth_token = undefined,
    x_origin = undefined,
}) => {
    const token =
        auth_token ||
        localStorage.getItem('respeak_widget_access_token') ||
        localStorage.getItem('access_token');

    const headers = {
        'Content-Type': 'application/json',
        'x-origin': x_origin,
    };

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    const admin_mode = localStorage.getItem('admin_mode');
    if (admin_mode) {
        headers['superuser_mode'] = admin_mode;
    }

    const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(data),
    }).catch((err) => {
        console.error('Error in creating initial request', err);
    });

    if (!response.ok) {
        const errorText = await response.text(); // Reads the text in the response body
        const error = JSON.parse(errorText);
        if (response.status === 418) {
            throw new Error(
                'The prompt triggers the OpenAI content filtering system. Please contact the administrator.',
            );
        }
        if (error && error.detail) {
            throw new Error(error.detail);
        }
        console.error('Unexpected error in ', response);
        throw new Error('Unexpected error occurred. Please try again later.');
    }

    const reader = response.body.getReader();
    let result = '';
    let prevDataBuffer = '';
    let first_chunk_data = '';
    let citedDataList = [];
    let isFirstChunk = true;
    let cursor = 0;
    const stream_speed = 5;
    // let chunkCount = 0
    let start = false;
    let finished = false;

    const cursor_interval = setInterval(async () => {
        if (!start || cursor >= result.length) return;

        const imageCheck = isCursorInsideImage(result, cursor);
        if (imageCheck.inside) {
            cursor = imageCheck.endPosition + 1;
        } else {
            cursor += 1;
        }

        dataStream(result.slice(0, cursor));

        if (finished && cursor >= result.length) {
            clearInterval(cursor_interval);
            if (finished_callback !== undefined) await finished_callback();

            for (let cited of citedDataList) {
                citedData(cited);
            }
        }
    }, stream_speed);

    async function processIncomingData(received_data) {
        const { buffer, json } = processData({ buffer: prevDataBuffer, data: received_data });
        prevDataBuffer = buffer;

        for (let i = 0; i < json.length; i++) {
            const parsed_data = json[i];
            if (_.isNil(parsed_data)) continue;

            if (parsed_data.type === 'text') {
                if (!_.isNil(parsed_data.content)) {
                    result += parsed_data.content;
                }
            } else if (parsed_data.type === 'citation') {
                for (let element of parsed_data.content) {
                    const cited = JSON.parse(element);

                    // check if cursor is at the end
                    if (cursor >= result.length) {
                        // iterate over first citedDataList
                        while (citedDataList.length > 0) {
                            citedData(citedDataList.shift());
                        }
                        citedData(cited);
                    } else {
                        citedDataList.push(cited);
                    }
                }
            } else if (parsed_data.type === 'error') {
                toast.error(parsed_data.content, {
                    position: 'top-right',
                    duration: 5000,
                });
                if (parsed_data.error_code === generatorErrorCode.FILTER) {
                    result = 'none';
                }
                if (parsed_data.content) await no_answer_callback();
            }
        }
    }

    const readChunk = async () => {
        const { done, value } = await reader.read();
        if (done) {
            finished = true;

            if (result) {
                // return everything after <ans> if <ans> is present in the answer if <res> is present return nothing after <res> and before <ans> otherwise return everything after <res>
                const { answerStart: ans_start, hasNoneAnswer } = extractAnswerAndReasoning(result);
                if (hasNoneAnswer) {
                    if (!no_rag_fallback) {
                        let lang = response_language.lang;

                        if (lang === 'auto') {
                            lang = query_language;
                        }

                        if (!no_answers_strings[lang]) {
                            lang = 'en';
                        }

                        const no_answer_string =
                            no_answers_strings[lang][
                                Math.floor(Math.random() * no_answers_strings[lang].length)
                            ];

                        if (ans_start !== -1) {
                            // replace <ans>...</ans> with no answer string
                            result = result.slice(0, ans_start + 5) + no_answer_string;
                        } else {
                            // append no answer string to the end of the result
                            result = no_answer_string;
                        }
                    } else {
                        // If no_rag_fallback is true, just remove 'none' from <ans> part
                        if (ans_start !== -1) {
                            result = result.slice(0, ans_start + 5);
                        } else {
                            // try to remove none from all
                            result = result.replace('none', '');
                        }
                    }
                    await no_answer_callback();
                }
            }

            if (cursor >= result.length) clearInterval(cursor_interval);

            return;
        }
        const received_data = new TextDecoder().decode(value);
        if (isFirstChunk && expect_first_chunk) {
            first_chunk_data += received_data;
            if (first_chunk_data.includes('\n')) {
                const json_data = first_chunk_data
                    .split('\n')
                    .filter((jsonStr) => jsonStr.trim() !== '');
                isFirstChunk = false;
                let init_data = JSON.parse(json_data[0]);
                initialData(init_data);

                if (json_data.length > 1) {
                    // join the rest of the chunks
                    start = true;
                    await processIncomingData(json_data.slice(1).join('\n'));
                }
            }
        } else {
            start = true;
            await processIncomingData(received_data);
        }
        await readChunk(); // Recursively read the next chunk
    };

    await readChunk(); // Start reading chunks
    return result;
};

export function fakeStreamText(text, interval, callback, finished_callback = undefined) {
    let index = 0;

    const intervalId = setInterval(async () => {
        if (index <= text.length) {
            callback(text.slice(0, index));
            index++;
        } else {
            clearInterval(intervalId);
            if (finished_callback !== undefined && typeof finished_callback === 'function') {
                await finished_callback();
            }
        }
    }, interval);
}
